import { Page, PageTitle } from "@/components/page"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { zodResolver } from "@hookform/resolvers/zod"
import { Shield } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { useState, useEffect } from "react"
import { supabase } from "@/lib/data"
import { toast } from "sonner"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { QRCodeSVG } from "qrcode.react"
import { useNavigate } from "react-router-dom"

export const mfaEnroll = {
  title: "Enable Two-Factor Authentication",
  description: "Set up 2FA to secure your account.",
  route: "/settings/mfa/enroll",
  icon: Shield,
  component,
} satisfies PageConfig

const enrollSchema = z.object({
  code: z.string().min(6, "Code must be 6 digits").max(6, "Code must be 6 digits"),
})

type EnrollFormData = z.infer<typeof enrollSchema>

function component() {
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [factorId, setFactorId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [enrolling, setEnrolling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const navigate = useNavigate()

  const form = useForm<EnrollFormData>({
    resolver: zodResolver(enrollSchema),
    defaultValues: {
      code: "",
    },
  })

  useEffect(() => {
    const startEnrollment = async () => {
      setLoading(true)
      setError(null)
      try {
        const { data: factorsData } = await supabase.auth.mfa.listFactors()
        const allFactors = [...(factorsData?.totp || []), ...(factorsData?.phone || [])]
        const verifiedFactors = allFactors.filter((f) => f.status === "verified")

        if (verifiedFactors.length > 0) {
          setError("You already have MFA enabled. Please remove existing factors first.")
          setLoading(false)
          return
        }

        const allFactorsWithName = allFactors.filter((f) => f.friendly_name === "Authenticator App")

        for (const factor of allFactorsWithName) {
          const { error: unenrollError } = await supabase.auth.mfa.unenroll({ factorId: factor.id })
          if (unenrollError) {
            console.error("Failed to unenroll factor:", unenrollError, factor)
          }
        }

        await new Promise(resolve => setTimeout(resolve, 1000))

        const { data: refreshedFactorsData } = await supabase.auth.mfa.listFactors()
        const refreshedFactors = [...(refreshedFactorsData?.totp || []), ...(refreshedFactorsData?.phone || [])]

        let friendlyName = "Authenticator App"
        let nameCounter = 1

        const allFactorNames = refreshedFactors.map((f) => f.friendly_name).filter(Boolean)
        while (allFactorNames.includes(friendlyName)) {
          friendlyName = `Authenticator App ${nameCounter}`
          nameCounter++
        }

        const { data, error } = await supabase.auth.mfa.enroll({
          factorType: "totp",
          friendlyName,
        })

        if (error) {
          if (error.message.includes("mfa_factor_name_conflict")) {
            setError("A factor with this name already exists. Please remove it first or use a different name.")
          } else {
            setError(error.message || "Failed to start enrollment")
          }
          return
        }

        if (data.totp) {
          if (data.totp.uri) {
            setQrCode(data.totp.uri)
          } else if (data.totp.qr_code) {
            if (data.totp.qr_code.startsWith("data:")) {
              setQrCodeImage(data.totp.qr_code)
            } else {
              setQrCode(data.totp.qr_code)
            }
          }
          setSecret(data.totp.secret)
        }
        setFactorId(data.id)
      } catch (error) {
        toast.error("An unexpected error occurred")
      } finally {
        setLoading(false)
      }
    }

    startEnrollment()
  }, [])

  async function onSubmit(data: EnrollFormData) {
    if (!factorId) {
      toast.error("Enrollment not initialized")
      return
    }

    setEnrolling(true)

    try {
      const { data: verifyData, error: verifyError } = await supabase.auth.mfa.challenge({
        factorId,
      })

      if (verifyError) {
        toast.error(verifyError.message || "Failed to create challenge")
        return
      }

      const { error: verifyCodeError } = await supabase.auth.mfa.verify({
        challengeId: verifyData.id,
        factorId,
        code: data.code,
      })

      if (verifyCodeError) {
        toast.error(verifyCodeError.message || "Invalid verification code")
        form.setError("code", { message: "Invalid code" })
        return
      }

      await supabase.auth.refreshSession()
      toast.success("Two-factor authentication enabled successfully")
      setSuccess(true)

      setTimeout(() => {
        navigate("/settings/profile")
      }, 1500)
    } catch (error) {
      toast.error("An unexpected error occurred")
    } finally {
      setEnrolling(false)
    }
  }

  return (
    <Page>
      <div className="pb-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <PageTitle />

            <div className="rgrid">
              <div className="row">
                <div className="rc-12">
                  {loading ? (
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-center">Initializing enrollment...</div>
                      </CardContent>
                    </Card>
                  ) : (qrCode || qrCodeImage) && secret ? (
                    <Card>
                      <CardHeader>
                        <CardTitle>Scan QR Code</CardTitle>
                        <CardDescription>
                          Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex justify-center">
                          <div className="p-4 bg-white rounded-lg border-2">
                            {qrCodeImage ? (
                              <img src={qrCodeImage} alt="QR Code" className="w-[200px] h-[200px]" />
                            ) : qrCode ? (
                              <QRCodeSVG value={qrCode} size={200} />
                            ) : null}
                          </div>
                        </div>
                        <div className="text-center space-y-2">
                          <p className="text-sm text-gray-600">Or enter this code manually:</p>
                          <code className="block p-2 bg-gray-100 rounded text-sm font-mono">
                            {secret}
                          </code>
                        </div>
                        <FormField
                          control={form.control}
                          name="code"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Verification Code</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="text"
                                  placeholder="Enter 6-digit code"
                                  maxLength={6}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, "").slice(0, 6)
                                    field.onChange(value)
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Button type="submit" disabled={enrolling} className="w-full">
                          {enrolling ? "Verifying..." : "Verify and Enable"}
                        </Button>
                      </CardContent>
                    </Card>
                  ) : error ? (
                    <Card>
                      <CardHeader>
                        <CardTitle>Enrollment Error</CardTitle>
                        <CardDescription>{error}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <Button
                          onClick={() => navigate("/settings/mfa")}
                          className="w-full"
                        >
                          Go to MFA Settings
                        </Button>
                        <Button
                          variant="outline"
                          onClick={async () => {
                            setError(null)
                            setLoading(true)
                            try {
                              const { data: factorsData } = await supabase.auth.mfa.listFactors()
                              const allFactors = [...(factorsData?.totp || []), ...(factorsData?.phone || [])]
                              //@ts-ignore
                              const existingUnverified = allFactors.filter((f) => f.status === "unverified")

                              for (const factor of existingUnverified) {
                                await supabase.auth.mfa.unenroll({ factorId: factor.id })
                              }

                              const remainingFactors = allFactors.filter((f) => f.status === "verified")
                              let friendlyName = "Authenticator App"
                              let nameCounter = 1

                              while (remainingFactors.some((f) => f.friendly_name === friendlyName)) {
                                friendlyName = `Authenticator App ${nameCounter}`
                                nameCounter++
                              }

                              const { data, error } = await supabase.auth.mfa.enroll({
                                factorType: "totp",
                                friendlyName,
                              })

                              if (error) {
                                setError(error.message || "Failed to start enrollment")
                                setLoading(false)
                                return
                              }

                              if (data.totp) {
                                if (data.totp.uri) {
                                  setQrCode(data.totp.uri)
                                } else if (data.totp.qr_code) {
                                  if (data.totp.qr_code.startsWith("data:")) {
                                    setQrCodeImage(data.totp.qr_code)
                                  } else {
                                    setQrCode(data.totp.qr_code)
                                  }
                                }
                                setSecret(data.totp.secret)
                              }
                              setFactorId(data.id)
                              setError(null)
                            } catch (error) {
                              setError("An unexpected error occurred")
                            } finally {
                              setLoading(false)
                            }
                          }}
                          className="w-full"
                        >
                          Try Again
                        </Button>
                      </CardContent>
                    </Card>
                  ) : success ? (
                    <Card>
                      <CardHeader>
                        <CardTitle>Success!</CardTitle>
                        <CardDescription>
                          Two-factor authentication has been enabled successfully. Redirecting to profile...
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  ) : (
                    <Card>
                      <CardContent className="pt-6">
                        <div className="text-center">Enrollment completed or failed to initialize.</div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </Page>
  )
}

